import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  listMappings,
  updateMapping,
  previewMapping,
  listValueLookups,
} from '../../services/syncApi';

const TRANSFORMS = [
  'identity', 'lowercase', 'uppercase', 'str_or_none',
  'int_or_none', 'bool_to_visible', 'always_true', 'always_false',
  'join_categories', 'strip_html_to_text',
  // Named-table lookup; mapping must also point at a value_lookup_id.
  // The save handler refuses to submit without a chosen lookup.
  'value_lookup',
];

const BC_TARGETS = ['core', 'custom_field', 'channel', 'category'];

// Channel + category mappings drive the per-store assignment patches, not the
// per-field PUT pipeline; toggling Enabled on them only affects whether the
// compare engine surfaces them in the diff view. Surfacing this in-table
// prevents users from thinking they can stop pushing channel assignments by
// flipping the row off.
const COMPARE_ONLY_TARGETS = new Set(['channel', 'category']);

const emptyDraft = (m) => ({
  id: m.id,
  version: m.version,
  akeneo_attribute: m.akeneo_attribute || '',
  postgres_column: m.postgres_column,
  bc_target: m.bc_target,
  bc_field_name: m.bc_field_name,
  market_scoped: m.market_scoped,
  transform: m.transform,
  enabled: m.enabled,
  // For transform='value_lookup' mappings only; null otherwise.
  value_lookup_id: m.value_lookup_id ?? null,
  // Snapshot of the on-server value to detect renames at save time.
  _server_bc_field_name: m.bc_field_name,
});

// Lightweight inline confirm popover for the row-level Enabled toggle.
// Renders absolute-positioned relative to its trigger row so it doesn't
// hijack focus the way window.confirm() does.
const TogglePopover = ({ mapping, nextEnabled, previewState, onCancel, onConfirm, busy }) => (
  <div className="mapping-toggle-popover" role="dialog" aria-label="Confirm enabled toggle">
    <div className="mapping-toggle-popover-body">
      <strong>{nextEnabled ? 'Enable' : 'Disable'}</strong>{' '}
      <code>{mapping.bc_target} / {mapping.bc_field_name}</code>?
      {previewState === 'loading' && <div className="muted">Calculating impact…</div>}
      {previewState && previewState.error && (
        <div className="error">Preview failed: {previewState.error}</div>
      )}
      {previewState && previewState.data && (
        <div className="muted">
          ~{previewState.data.approx_affected_skus} SKU(s) affected on next sync.
          {!nextEnabled && ' Existing values in BigCommerce will be left as-is (no delete).'}
        </div>
      )}
    </div>
    <div className="mapping-toggle-popover-actions">
      <button type="button" className="ghost" onClick={onCancel} disabled={busy}>Cancel</button>
      <button type="button" className="primary" onClick={onConfirm} disabled={busy || previewState?.loading}>
        {busy ? 'Saving…' : nextEnabled ? 'Enable' : 'Disable'}
      </button>
    </div>
  </div>
);

const MappingsEditor = () => {
  const [mappings, setMappings] = useState([]);
  // Loaded once on mount; lightweight (~10s of rows) and only used as a
  // dropdown source for the value_lookup transform.
  const [valueLookups, setValueLookups] = useState([]);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Per-row inline toggle state: { mappingId, nextEnabled, previewState, busy }
  const [pendingToggle, setPendingToggle] = useState(null);
  // Banner shown after a rename so the user sees the orphaned-field note.
  const [renameNotice, setRenameNotice] = useState(null);
  const previewSeq = useRef(0);

  const load = useCallback(async () => {
    setMappings(await listMappings());
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Best-effort: the page is still useful without lookups loaded.
    listValueLookups().then(setValueLookups).catch(() => setValueLookups([]));
  }, []);

  const lookupNameById = useCallback(
    (id) => valueLookups.find((l) => l.id === id)?.name,
    [valueLookups],
  );

  const startEdit = (m) => {
    setDraft(emptyDraft(m));
    setPreview(null);
    setError(null);
    setRenameNotice(null);
  };

  const handlePreview = async () => {
    try {
      const r = await previewMapping(draft);
      setPreview(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Mirror the backend MappingUpdate validation so the user sees the
  // problem in the form before eating a 400.
  const valueLookupRequired =
    draft?.transform === 'value_lookup' && !draft?.value_lookup_id;

  const save = async () => {
    if (valueLookupRequired) {
      setError('value_lookup transform requires a Lookup selection.');
      return;
    }
    const renamed = draft.bc_field_name !== draft._server_bc_field_name;
    if (renamed) {
      const proceed = window.confirm(
        `BC field name will change from "${draft._server_bc_field_name}" to "${draft.bc_field_name}".\n\n` +
        `The old field will remain in BigCommerce as an orphan (we don't delete custom fields).\n\n` +
        `Continue?`,
      );
      if (!proceed) return;
    }
    if (!preview) {
      const proceed = window.confirm('No preview run yet — save anyway?');
      if (!proceed) return;
    } else {
      const proceed = window.confirm(
        `This will affect approximately ${preview.approx_affected_skus} SKU(s) on next sync. Continue?`,
      );
      if (!proceed) return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateMapping(draft.id, draft.version, {
        akeneo_attribute: draft.akeneo_attribute || null,
        postgres_column: draft.postgres_column,
        bc_target: draft.bc_target,
        bc_field_name: draft.bc_field_name,
        market_scoped: draft.market_scoped,
        transform: draft.transform,
        enabled: draft.enabled,
        // Only send value_lookup_id for value_lookup mappings -- other
        // transforms ignore it on the server but sending null keeps the
        // payload shape consistent and clears any stale FK if the user
        // switched away from value_lookup.
        value_lookup_id:
          draft.transform === 'value_lookup' ? draft.value_lookup_id : null,
      });
      if (renamed) {
        setRenameNotice({
          oldName: draft._server_bc_field_name,
          newName: draft.bc_field_name,
        });
      }
      setDraft(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Per-row Enabled toggle: open popover with live preview before committing.
  const requestToggle = async (m) => {
    const nextEnabled = !m.enabled;
    const seq = ++previewSeq.current;
    setPendingToggle({ mappingId: m.id, nextEnabled, previewState: { loading: true }, busy: false });

    try {
      const r = await previewMapping({
        id: m.id,
        version: m.version,
        akeneo_attribute: m.akeneo_attribute,
        postgres_column: m.postgres_column,
        bc_target: m.bc_target,
        bc_field_name: m.bc_field_name,
        market_scoped: m.market_scoped,
        transform: m.transform,
        enabled: nextEnabled,
      });
      if (previewSeq.current !== seq) return;
      setPendingToggle((p) => p && p.mappingId === m.id
        ? { ...p, previewState: { data: r } }
        : p);
    } catch (e) {
      if (previewSeq.current !== seq) return;
      setPendingToggle((p) => p && p.mappingId === m.id
        ? { ...p, previewState: { error: e.response?.data?.detail || e.message } }
        : p);
    }
  };

  const confirmToggle = async (m) => {
    if (!pendingToggle || pendingToggle.mappingId !== m.id) return;
    setPendingToggle((p) => ({ ...p, busy: true }));
    try {
      await updateMapping(m.id, m.version, {
        akeneo_attribute: m.akeneo_attribute,
        postgres_column: m.postgres_column,
        bc_target: m.bc_target,
        bc_field_name: m.bc_field_name,
        market_scoped: m.market_scoped,
        transform: m.transform,
        enabled: pendingToggle.nextEnabled,
      });
      setPendingToggle(null);
      load();
    } catch (e) {
      setPendingToggle((p) => p ? { ...p, busy: false, previewState: { error: e.response?.data?.detail || e.message } } : null);
    }
  };

  const cancelToggle = () => setPendingToggle(null);

  return (
    <div className="mappings-editor">
      <header><h1>Field Mappings</h1></header>

      {renameNotice && (
        <div className="card mapping-rename-notice">
          <strong>Renamed:</strong>{' '}
          <code>{renameNotice.oldName}</code> → <code>{renameNotice.newName}</code>.
          The previous BigCommerce field <code>{renameNotice.oldName}</code> will be left
          orphaned (no delete). Clear it manually in BigCommerce if you don't want it shown.
          <button type="button" className="ghost" onClick={() => setRenameNotice(null)}>Dismiss</button>
        </div>
      )}

      <section className="card mappings-table-card">
        <table className="mappings-table">
          <thead>
            <tr>
              <th className="sticky-col col-bc-field">BC field</th>
              <th className="sticky-col col-actions">Actions</th>
              <th>BC target</th>
              <th>Postgres column</th>
              <th>Transform</th>
              <th>Market-scoped</th>
              <th>Enabled</th>
              <th>v</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => {
              const isCompareOnly = COMPARE_ONLY_TARGETS.has(m.bc_target);
              const isPending = pendingToggle?.mappingId === m.id;
              return (
                <tr key={m.id} className={isCompareOnly ? 'row-compare-only' : ''}>
                  <td className="sticky-col col-bc-field">
                    <code>{m.bc_field_name}</code>
                    {isCompareOnly && (
                      <span className="mapping-badge" title="Channel/category assignments are pushed by dedicated patches; the Enabled toggle here only affects how this row appears in compare/UI.">
                        Compare/UI only
                      </span>
                    )}
                  </td>
                  <td className="sticky-col col-actions">
                    <button type="button" className="link" onClick={() => startEdit(m)}>Edit</button>
                  </td>
                  <td>{m.bc_target}</td>
                  <td><code className="muted">{m.postgres_column}</code></td>
                  <td>
                    {m.transform}
                    {m.transform === 'value_lookup' && m.value_lookup_id && (
                      <span className="muted">
                        {' '}→ <code>{lookupNameById(m.value_lookup_id) || `#${m.value_lookup_id}`}</code>
                      </span>
                    )}
                  </td>
                  <td>{m.market_scoped ? 'yes' : 'no'}</td>
                  <td className="col-enabled">
                    <label className="mapping-toggle">
                      <input
                        type="checkbox"
                        checked={isPending ? pendingToggle.nextEnabled : m.enabled}
                        onChange={() => requestToggle(m)}
                        disabled={isPending && pendingToggle.busy}
                        aria-label={`${m.enabled ? 'Disable' : 'Enable'} mapping ${m.bc_field_name}`}
                      />
                    </label>
                    {isPending && (
                      <TogglePopover
                        mapping={m}
                        nextEnabled={pendingToggle.nextEnabled}
                        previewState={pendingToggle.previewState}
                        busy={pendingToggle.busy}
                        onCancel={cancelToggle}
                        onConfirm={() => confirmToggle(m)}
                      />
                    )}
                  </td>
                  <td className="col-version">{m.version}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {draft && (
        <section className="card mapping-edit-card">
          <h2>Edit mapping #{draft.id} <small>v{draft.version}</small></h2>
          {error && <p className="error">{error}</p>}
          {COMPARE_ONLY_TARGETS.has(draft.bc_target) && (
            <p className="muted mapping-edit-note">
              This is a {draft.bc_target} mapping. The Enabled toggle only affects
              how it appears in compare/UI; the actual assignment is driven by the
              {draft.bc_target === 'channel' ? ' ChannelAssignmentPatch' : ' CategoryAssignmentPatch'}.
            </p>
          )}
          <div className="grid-2">
            <label>BC target
              <select value={draft.bc_target} onChange={(e) => setDraft({ ...draft, bc_target: e.target.value })}>
                {BC_TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>BC field name
              <input
                value={draft.bc_field_name}
                onChange={(e) => setDraft({ ...draft, bc_field_name: e.target.value })}
              />
              {draft.bc_field_name !== draft._server_bc_field_name && (
                <span className="muted mapping-rename-hint">
                  Old field <code>{draft._server_bc_field_name}</code> will stay in BC as orphan.
                </span>
              )}
            </label>
            <label>Postgres column
              <input value={draft.postgres_column} onChange={(e) => setDraft({ ...draft, postgres_column: e.target.value })} />
            </label>
            <label>Akeneo attribute
              <input value={draft.akeneo_attribute} onChange={(e) => setDraft({ ...draft, akeneo_attribute: e.target.value })} />
            </label>
            <label>Transform
              <select value={draft.transform} onChange={(e) => setDraft({ ...draft, transform: e.target.value })}>
                {TRANSFORMS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            {draft.transform === 'value_lookup' && (
              <label>Lookup
                <select
                  value={draft.value_lookup_id ?? ''}
                  onChange={(e) => setDraft({
                    ...draft,
                    value_lookup_id: e.target.value ? Number(e.target.value) : null,
                  })}
                >
                  <option value="">— select a lookup —</option>
                  {valueLookups.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.entry_count} entr{l.entry_count === 1 ? 'y' : 'ies'})
                    </option>
                  ))}
                </select>
                <span className="muted">
                  Manage lookup contents in the Value Lookups page.
                </span>
              </label>
            )}
            <label>
              <input type="checkbox" checked={draft.market_scoped} onChange={(e) => setDraft({ ...draft, market_scoped: e.target.checked })} />
              Market scoped
            </label>
            <label>
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>
          <div className="actions">
            <button type="button" onClick={handlePreview}>Preview impact</button>
            {preview && <span className="muted"> ~{preview.approx_affected_skus} SKU(s) affected</span>}
            <button
              type="button"
              onClick={save}
              disabled={saving || valueLookupRequired}
              title={valueLookupRequired ? 'Choose a lookup for the value_lookup transform' : ''}
            >
              Save
            </button>
            <button type="button" onClick={() => { setDraft(null); setPreview(null); }}>Cancel</button>
          </div>
        </section>
      )}
    </div>
  );
};

export default MappingsEditor;
